import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { describeError } from "../api/http";
import {
  useAppState,
  usePlan,
  useRecommendLineup,
  useSettings,
} from "../api/queries";
import type { LineupSlot } from "../api/types";
import { Page } from "../components/layout/Page";
import { Pitch, type PitchPlayer } from "../components/Pitch";
import { Stat } from "../components/Scoreline";
import { Button } from "../components/ui/Button";
import {
  PositionChip,
  StatusChip,
  ToggleChip,
  STATUS_LABELS,
  type Status,
} from "../components/ui/Chip";
import { Banner, EmptyState, ErrorState } from "../components/ui/ErrorState";
import { Skeleton } from "../components/ui/Skeleton";
import { useToast } from "../components/ui/Toast";
import { points } from "../lib/format";
import { parseFormation, POSITIONS, type Position } from "../lib/positions";

/**
 * Pantalla *Alineación* — M8-T06 (UC-08.06).
 *
 * QUÉ HACE
 *   Pide a `POST /api/recommend/lineup` el once de la próxima jornada (sección 7.4) y lo enseña
 *   sobre el campo a tamaño completo, que es el elemento firma de la sección 8.4. Debajo, los
 *   avisos del servidor y el banquillo ordenado. La única acción de la pantalla es llevarse el
 *   once escrito: *Copiar resumen*.
 *
 * EL SELECTOR DE FORMACIÓN NO VUELVE A PREGUNTAR AL SERVIDOR
 *   Lo pide la descripción de la tarea, y tiene su razón: la respuesta ya trae **a toda la
 *   plantilla** valorada con `round_expected` —los once titulares y el banquillo entero—, así que
 *   recomponer el once para otra formación es repartir esas mismas cifras de otra manera. Una
 *   segunda llamada devolvería exactamente los mismos números después de recalcular las métricas
 *   de media liga. Se recompone en el cliente y se responde en el acto.
 *
 *   Solo se ofrecen las formaciones de `league.formations` (sección 5.2), y las que la plantilla
 *   no puede completar se quedan deshabilitadas con el motivo escrito: un campo con huecos no es
 *   una alineación.
 *
 * POR QUÉ EL ONCE SE PIDE EN UN EFECTO
 *   Igual que en *Inicio*: la ruta lleva cuerpo, así que la capa de datos la expone como mutación
 *   y no como consulta. Se lanza al montar y vuelve a lanzarse cuando cambia la «foto» de los
 *   datos —una sincronización nueva— o cuando se enciende el interruptor *Con plan*, que cambia
 *   la plantilla de partida (sección 7.5).
 *
 * LOS AVISOS SE VEN DOS VECES
 *   Es la aceptación de la tarea, y son dos lecturas distintas: la ficha del jugador afectado va
 *   marcada en el campo —contorno `money` y glifo, nunca solo el color— con su motivo en la
 *   etiqueta, y en el banquillo el motivo va escrito en un chip; la lista de avisos, aparte,
 *   cuenta lo que no cabe en una ficha (qué equipos descansan, qué huecos no se han cubierto).
 */

/* ==================================================================== las cuentas del once = */

/** Lo que suma un conjunto de huecos: la cifra de marcador de la pantalla. */
export function puntosEsperados(slots: readonly LineupSlot[]): number {
  return slots.reduce((total, slot) => total + slot.expected, 0);
}

/**
 * Orden de una posición: primero quien más puntos esperados trae y, a igualdad, el identificador
 * más bajo. Es el mismo desempate que usa el servidor (sección 7.3.6), de modo que recomponer el
 * once en el cliente con la formación que ya venía elegida devuelve exactamente el mismo once.
 */
function mejorPrimero(uno: LineupSlot, otro: LineupSlot): number {
  const porPuntos = otro.expected - uno.expected;
  return porPuntos !== 0 ? porPuntos : uno.player.id - otro.player.id;
}

export interface OnceCompuesto {
  /** Los once titulares, ordenados por posición (POR, DEF, MED, DEL), como el servidor. */
  starters: LineupSlot[];
  /** El resto, de mayor a menor `round_expected`. */
  bench: LineupSlot[];
}

/**
 * Reparte a toda la plantilla en once y banquillo para una formación concreta.
 *
 * Devuelve `null` —y no un once a medias— cuando la formación no se puede leer o cuando falta
 * gente en alguna posición: es lo que permite al selector deshabilitar las formaciones
 * imposibles en lugar de dibujar un campo con huecos.
 */
export function onceParaFormacion(
  slots: readonly LineupSlot[],
  formation: string,
): OnceCompuesto | null {
  const leida = parseFormation(formation);
  if (leida === null) return null;

  const porPosicion = new Map<Position, LineupSlot[]>();
  for (const slot of slots) {
    const cola = porPosicion.get(slot.player.position) ?? [];
    cola.push(slot);
    porPosicion.set(slot.player.position, cola);
  }

  const starters: LineupSlot[] = [];
  const bench: LineupSlot[] = [];

  for (const position of POSITIONS) {
    const cola = [...(porPosicion.get(position) ?? [])].sort(mejorPrimero);
    const cuantos = leida.counts[position];
    if (cola.length < cuantos) return null;
    starters.push(...cola.slice(0, cuantos));
    bench.push(...cola.slice(cuantos));
  }

  return { starters, bench: bench.sort(mejorPrimero) };
}

/* ========================================================================= el resumen ==== */

export interface ResumenDelOnce {
  roundNumber: number | null;
  formation: string;
  starters: readonly LineupSlot[];
}

/** «Jornada 13» o, sin calendario, «Próxima jornada» (sección 7.4). */
function tituloDeLaJornada(roundNumber: number | null): string {
  return roundNumber === null
    ? "Próxima jornada"
    : `Jornada ${String(roundNumber)}`;
}

/**
 * El once en texto plano, listo para pegarlo en Biwenger o en un chat: la jornada, la formación,
 * los puntos esperados y las once líneas con su posición, su nombre, sus puntos y, si lo tiene,
 * el aviso entre paréntesis. Sin adornos ni tabuladores: se lee igual en cualquier sitio.
 */
export function resumenEnTexto(resumen: ResumenDelOnce): string {
  const cabecera = `Alineación · ${tituloDeLaJornada(resumen.roundNumber)} · ${resumen.formation}`;
  const total = `Puntos esperados: ${points(puntosEsperados(resumen.starters))}`;
  const lineas = resumen.starters.map((slot) => {
    const aviso = slot.warning === undefined ? "" : ` (${slot.warning})`;
    return `${slot.player.position} · ${slot.player.name} · ${points(slot.expected)}${aviso}`;
  });
  return [cabecera, total, "", ...lineas].join("\n");
}

/**
 * Copia al portapapeles.
 *
 * `navigator.clipboard` no siempre está: en un contexto sin HTTPS el navegador no lo expone, y
 * entonces el respaldo es el de toda la vida —un campo de texto fuera de pantalla, seleccionarlo
 * y `execCommand`—, que sigue funcionando en todos los navegadores de escritorio y móvil. Si
 * ninguno de los dos caminos funciona se devuelve `false` y la pantalla lo dice: un botón que no
 * hace nada y no avisa es peor que un botón que falla.
 */
export async function copiarTexto(texto: string): Promise<boolean> {
  const portapapeles: Clipboard | undefined = navigator.clipboard;
  if (typeof portapapeles?.writeText === "function") {
    try {
      await portapapeles.writeText(texto);
      return true;
    } catch {
      // Sin permiso o fuera de un gesto del usuario: queda el respaldo.
    }
  }
  return copiarConSeleccion(texto);
}

function copiarConSeleccion(texto: string): boolean {
  const campo = document.createElement("textarea");
  campo.value = texto;
  campo.setAttribute("readonly", "");
  campo.setAttribute("aria-hidden", "true");
  campo.style.position = "fixed";
  campo.style.opacity = "0";
  document.body.appendChild(campo);
  try {
    campo.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    campo.remove();
  }
}

/* ========================================================================== vocabulario == */

/**
 * Del texto del aviso al estado que le corresponde. El servidor escribe el aviso con el
 * vocabulario de la sección 8.3 (*Lesionado*, *Duda*, *Sancionado*), que es exactamente el de los
 * chips de estado, así que se reconoce y se pinta el chip de siempre en vez de inventar otro.
 * Lo que no encaje —«No juega esta jornada», «Sin equipo»— sale con el chip genérico de aviso.
 */
const ESTADO_POR_AVISO = new Map<string, Status>(
  Object.entries(STATUS_LABELS).flatMap(([status, label]) =>
    label === null ? [] : [[label, status as Status] as [string, Status]],
  ),
);

/** El motivo por el que un jugador lastra la alineación, escrito y con glifo. */
function EtiquetaDeAviso({ warning }: { warning: string }) {
  const estado = ESTADO_POR_AVISO.get(warning);
  if (estado !== undefined) return <StatusChip status={estado} />;

  return (
    <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-money/40 bg-money/10 px-[7px] text-[11px] text-money">
      <span aria-hidden="true">!</span>
      {warning}
    </span>
  );
}

/* ============================================================================== piezas === */

interface TarjetaProps {
  titulo?: string;
  children: ReactNode;
}

function Tarjeta({ titulo, children }: TarjetaProps) {
  return (
    <section className="grid gap-3 rounded-xl border border-border bg-surface p-3">
      {titulo === undefined ? null : (
        <h2 className="text-[15px] leading-none text-ink">{titulo}</h2>
      )}
      {children}
    </section>
  );
}

/** La silueta de la pantalla mientras se calcula el once (sección 8.2). */
function Esqueleto() {
  return (
    <>
      <Skeleton variant="row" label="Calculando la alineación" />
      <Skeleton
        variant="pitch"
        label="Calculando el once de la próxima jornada"
      />
      <Skeleton variant="card" label="Calculando el banquillo" />
    </>
  );
}

/** Cada jugador del once, tal y como lo pinta el campo (sección 8.4). */
function aFichas(slots: readonly LineupSlot[]): PitchPlayer[] {
  return slots.map((slot) => ({
    id: slot.player.id,
    name: slot.player.name,
    position: slot.player.position,
    expected: slot.expected,
    state: slot.warning === undefined ? "normal" : "warn",
    warning: slot.warning ?? null,
  }));
}

interface BanquilloProps {
  slots: readonly LineupSlot[];
}

/** El banquillo, de mayor a menor `round_expected`: quién entraría si algo se tuerce. */
function Banquillo({ slots }: BanquilloProps) {
  if (slots.length === 0) {
    return (
      <Tarjeta titulo="Banquillo">
        <p className="text-xs text-muted">
          Toda tu plantilla está en el campo: no queda nadie en el banquillo.
        </p>
      </Tarjeta>
    );
  }

  return (
    <Tarjeta titulo="Banquillo">
      <ul className="grid list-none gap-2 p-0">
        {slots.map((slot) => (
          <li key={slot.player.id} className="flex items-center gap-2">
            <PositionChip position={slot.player.position} />
            <span className="min-w-0 flex-1 truncate text-[13px]">
              {slot.player.name}
            </span>
            {slot.warning === undefined ? null : (
              <EtiquetaDeAviso warning={slot.warning} />
            )}
            <span className="font-mono text-[13px] tabular-nums text-muted">
              {points(slot.expected)}
            </span>
          </li>
        ))}
      </ul>
    </Tarjeta>
  );
}

/* ============================================================================= pantalla == */

export function Lineup() {
  const { showToast } = useToast();
  const estado = useAppState();
  const ajustes = useSettings();
  const plan = usePlan();
  const alineacion = useRecommendLineup();

  const { mutate: pedirOnce, reset: olvidarOnce } = alineacion;
  const [conPlan, setConPlan] = useState(false);
  const [formacionElegida, setFormacionElegida] = useState<string | null>(null);
  const [falloAlCopiar, setFalloAlCopiar] = useState(false);
  const pedido = useRef<string | null>(null);

  /**
   * La «foto» de los datos: mientras no cambie, el once ya calculado sigue valiendo. Cambia con
   * cada sincronización nueva y al mover el interruptor *Con plan*, que son las dos cosas que
   * alteran la plantilla de partida.
   */
  const foto = estado.isPending
    ? null
    : `${String(estado.data?.syncedAt ?? null)}|${String(conPlan)}`;

  useEffect(() => {
    if (foto === null || pedido.current === foto) return;
    pedido.current = foto;
    olvidarOnce();
    // El once que llegue trae su propia formación óptima: la elección anterior ya no aplica.
    setFormacionElegida(null);
    pedirOnce({ plan: conPlan });
  }, [foto, conPlan, pedirOnce, olvidarOnce]);

  function reintentar(): void {
    olvidarOnce();
    pedirOnce({ plan: conPlan });
  }

  const datos = alineacion.data;
  const calculando = alineacion.isPending || alineacion.isIdle;

  /* ------------------------------------------------------- el once que se está mirando -- */

  const todos = useMemo<LineupSlot[]>(
    () => (datos === undefined ? [] : [...datos.starters, ...datos.bench]),
    [datos],
  );

  /** Las formaciones que se pueden elegir: las de la liga, más la que trajo el servidor. */
  const catalogo = useMemo(() => {
    const permitidas = ajustes.data?.league.formations ?? [];
    const propuesta = datos?.formation ?? "";
    return propuesta !== "" && !permitidas.includes(propuesta)
      ? [...permitidas, propuesta]
      : permitidas;
  }, [ajustes.data, datos]);

  /** Cada formación del catálogo con el once que daría, o `null` si no se puede completar. */
  const composiciones = useMemo(() => {
    const mapa = new Map<string, OnceCompuesto | null>();
    for (const formacion of catalogo) {
      mapa.set(formacion, onceParaFormacion(todos, formacion));
    }
    return mapa;
  }, [catalogo, todos]);

  const formacion = formacionElegida ?? datos?.formation ?? "";
  const compuesto =
    composiciones.get(formacion) ?? onceParaFormacion(todos, formacion);
  const titulares = compuesto?.starters ?? datos?.starters ?? [];
  const suplentes = compuesto?.bench ?? datos?.bench ?? [];
  const hayOnce = titulares.length > 0;
  const esLaOptima = datos === undefined || formacion === datos.formation;
  const hayImposibles = [...composiciones.values()].some((una) => una === null);

  /* ------------------------------------------------------------------------- copiar ----- */

  function copiar(): void {
    if (datos === undefined || !hayOnce) return;
    const texto = resumenEnTexto({
      roundNumber: datos.roundNumber,
      formation: formacion,
      starters: titulares,
    });
    void copiarTexto(texto).then((copiado) => {
      setFalloAlCopiar(!copiado);
      if (copiado) showToast("Copiado");
    });
  }

  /* --------------------------------------------------------------------- el cuerpo ------ */

  const avisos = datos?.warnings ?? [];
  const entradasDelPlan =
    (plan.data?.buys.length ?? 0) + (plan.data?.sells.length ?? 0);

  /** Qué se está mirando y por qué hay chips apagados, en una sola frase bajo el selector. */
  const pieDelSelector = [
    esLaOptima
      ? "Esta es la formación que más puntos esperados suma. Cambia de formación para ver otro reparto."
      : `Estás viendo otro reparto: la que más suma es ${datos?.formation ?? ""}.`,
    hayImposibles
      ? "Las formaciones atenuadas no las completa tu plantilla."
      : "",
  ]
    .filter((frase) => frase !== "")
    .join(" ");

  let cuerpo: ReactNode;
  if (calculando) {
    cuerpo = <Esqueleto />;
  } else if (datos === undefined) {
    cuerpo = (
      <ErrorState
        title="No se ha podido calcular la alineación"
        message={describeError(alineacion.error)}
        onRetry={reintentar}
      />
    );
  } else {
    cuerpo = (
      <>
        <Tarjeta>
          <div className="flex items-start justify-between gap-3">
            <div className="grid gap-0.5">
              <span className="text-[15px] font-medium leading-tight text-ink">
                {tituloDeLaJornada(datos.roundNumber)}
              </span>
              <span className="text-xs text-muted">
                {hayOnce
                  ? `${formacion} · once recomendado${conPlan ? ", con el plan aplicado" : ""}`
                  : "Sin once que proponer"}
              </span>
            </div>
            <Stat
              label="Puntos esperados"
              value={points(puntosEsperados(titulares))}
              size={26}
              className="justify-items-end text-right"
            />
          </div>

          <div className="flex min-h-touch items-center justify-between gap-3 border-t border-border pt-2">
            <label
              htmlFor="alineacion-con-plan"
              className="flex min-h-touch flex-1 cursor-pointer items-center text-[13px] font-medium text-ink"
            >
              Con plan
            </label>
            <input
              id="alineacion-con-plan"
              type="checkbox"
              checked={conPlan}
              onChange={(evento) => {
                setConPlan(evento.target.checked);
              }}
              aria-describedby="alineacion-con-plan-hint"
              className="h-6 w-11 shrink-0 accent-pitch"
            />
          </div>
          <p id="alineacion-con-plan-hint" className="-mt-2 text-xs text-muted">
            {entradasDelPlan > 0
              ? "Alinea la plantilla que dejaría tu plan, no la de hoy."
              : "Tu plan está vacío, así que da lo mismo: se alinea la plantilla de hoy."}
          </p>
        </Tarjeta>

        {hayOnce && catalogo.length > 0 ? (
          <div className="grid gap-1.5">
            <div
              role="group"
              aria-label="Formación"
              className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {catalogo.map((otra) => {
                const imposible = composiciones.get(otra) == null;
                return (
                  <ToggleChip
                    key={otra}
                    pressed={otra === formacion}
                    disabled={imposible}
                    title={
                      imposible
                        ? "No tienes jugadores suficientes para completar esta formación."
                        : undefined
                    }
                    onClick={() => {
                      setFormacionElegida(otra);
                    }}
                    className="font-mono tabular-nums"
                  >
                    {otra}
                  </ToggleChip>
                );
              })}
            </div>
            <p className="text-xs text-muted">{pieDelSelector}</p>
          </div>
        ) : null}

        {hayOnce ? (
          <Pitch formation={formacion} players={aFichas(titulares)} />
        ) : (
          <EmptyState
            title="Todavía no hay once"
            message="Tu plantilla no completa ninguna formación permitida. Sincroniza para traerla o revisa las formaciones en Ajustes."
          />
        )}

        {avisos.length > 0 ? (
          <Banner
            title={
              avisos.length === 1
                ? "1 aviso"
                : `${String(avisos.length)} avisos`
            }
          >
            <span className="grid gap-1.5">
              {avisos.map((aviso, indice) => (
                <span
                  key={`${String(indice)}-${aviso}`}
                  className="flex gap-1.5"
                >
                  <span aria-hidden="true">·</span>
                  <span>{aviso}</span>
                </span>
              ))}
              {esLaOptima ? null : (
                <span className="text-xs">
                  Se calcularon para la formación {datos.formation}.
                </span>
              )}
            </span>
          </Banner>
        ) : null}

        <Banquillo slots={suplentes} />

        {falloAlCopiar ? (
          <p role="alert" className="text-xs text-down">
            No se ha podido copiar el resumen: el navegador no lo ha permitido.
          </p>
        ) : null}
      </>
    );
  }

  return (
    <Page
      title="Alineación"
      action={
        <Button
          variant="primary"
          size="sm"
          disabled={!hayOnce}
          onClick={copiar}
        >
          Copiar resumen
        </Button>
      }
    >
      {cuerpo}
    </Page>
  );
}
